import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { ConsentContext } from '@context/ConsentContext'
import { useGotchiCelebration } from '@context/GotchiCelebrationContext'

import Button from '@components/Button/Button'
import Markdown from '@components/Markdown/Markdown'
import CheckboxRadio from '@components/CheckboxRadio/CheckboxRadio'
import ConsentProgressDemo from '@components/Consent/ConsentProgressDemo'
import { Pages } from '@models/Pages'
import { SurveyStep } from '@models/Survey/SurveyStep'
import './ConsentPage.scss'
import { SurveyContext } from '@context/SurveyContext'
import dayjs from '@utils/stats/date'

function ConsentPage() {
  const { t } = useTranslation('Consent')
  const navigate = useNavigate()
  const { playRain } = useGotchiCelebration()
  const {
    hasConsented,
    hasReadConsent,
    toggleConditions,
    toggleMajor,
    hasConditions,
    isMajor,
    checkConsents,
    sendConsents,
    toggleReadConsent,
  } = useContext(ConsentContext)
  const { setSurveyStartedAt, survey } = useContext(SurveyContext)
  const [collapsed, setCollapsed] = useState(true)
  const [readConsentErrorClass, setReadConsentErrorClass] = useState<string | undefined>()
  const [conditionClass, setConditionClass] = useState<string | undefined>()
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (hasConsented) {
      navigate(`/${Pages.Survey}/${SurveyStep.Answer}`)
    }
  }, [hasConsented, navigate])

  const handleAgeChange = () => {
    toggleMajor()
  }

  const handleConditionChange = () => {
    toggleConditions()
  }

  const checkIfCanProceed = useCallback(() => {
    if (!hasReadConsent) {
      setReadConsentErrorClass('error')
    }

    if (!hasConditions) {
      setConditionClass('error')
    }

    if (!hasReadConsent || !hasConditions) {
      return false
    }

    return true
  }, [hasReadConsent, hasConditions])

  // If we access this page from a direct link, we check if we already consented before
  if (!hasConsented) {
    checkConsents()
  }

  const collapsedClass = collapsed && 'collapsed'

  return (
    <div className={`Consent ${collapsedClass}`}>
      <header>
        <h2>{t('title')}</h2>
        <hr />
      </header>

      <div className="Consent-content">
        <div className="Consent-sectionLead">
          <button
            type="button"
            className="Consent-toggleLink"
            onClick={() => setCollapsed((value) => !value)}
          >
            {collapsed ? t('showAll') : t('hideAll')}
          </button>
          <Markdown content={t('consentText')} />
        </div>
        <CheckboxRadio
          propClassName={`Consent-ReadConsent Consent-check Consent-check--green ${readConsentErrorClass ?? ''}`}
          label={t('consentCheckbox')}
          value={hasReadConsent}
          onChange={() => {
            toggleReadConsent()
            setReadConsentErrorClass(undefined)
          }}
        />
        <Markdown content={t('text')} className="Consent-text" />
        {collapsed && (
          <div className="offset">
            <Button
              onClick={() => {
                setCollapsed(false)
              }}>
              {t('showAll')}
            </Button>
          </div>
        )}
        {!collapsed && (
          <button
            type="button"
            className="Consent-collapseBar"
            onClick={() => setCollapsed(true)}
            aria-label={t('hideAll')}
          >
            <span className="Consent-collapseBar-line" aria-hidden />
            <span className="Consent-collapseBar-x" aria-hidden>
              ×
            </span>
            <span className="Consent-collapseBar-line" aria-hidden />
          </button>
        )}
      </div>

      <footer>
        <div className="Consent-ageBlock">
          <CheckboxRadio
            label=""
            value={isMajor}
            onChange={handleAgeChange}
            propClassName="ConsentCheckboxCustomMargin Consent-ageCheckbox Consent-check Consent-check--blue"
          />
          <div className="Consent-ageBlock-texts" onClick={handleAgeChange} role="presentation">
            <span className="Consent-ageBlock-label">{t('age')}</span>
            <p
              className={[
                'Consent-ageNote',
                isMajor ? 'Consent-ageNote--dismissed' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {t('ageUnder18Note')}
            </p>
          </div>
        </div>
        <CheckboxRadio
          propClassName={`ConsentCheckboxCustomMargin Consent-check Consent-check--green ${conditionClass ?? ''}`}
          label={t('accept')}
          value={hasConditions}
          onChange={() => {
            handleConditionChange()
            setConditionClass(undefined)
          }}
        />

        <ConsentProgressDemo />
      </footer>

      <div className="Consent-submit">
        <Link to={`/${Pages.Home}`}>
          <strong>{t('cancel')}</strong>
        </Link>

        <Button
          disabled={!survey || isSubmitting}
          onClick={async () => {
            setIsSubmitting(true)

            if (!checkIfCanProceed() || !survey) {
              setIsSubmitting(false)
              return
            }

            playRain()
            await sendConsents(survey.id)
            setSurveyStartedAt(dayjs().toISOString())
            setIsSubmitting(false)
            // Navigation follows hasConsented via useEffect — does not wait for rain.
          }}>
          <span>{t('start')}</span>
        </Button>
      </div>
    </div>
  )
}

export default ConsentPage
